import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormRoot, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { QueryState } from "@/shared/ui/QueryState";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { formatSupplierApiError } from "../api/suppliers-client";
import { useSupplierQuery, useUpdateSupplier } from "../api/suppliers-queries";
import { SUPPLIER_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";
import {
  SupplierFormCard,
  type SupplierFormValues,
} from "../ui/SupplierFormCard";

const FormSchema = z.object({
  name: z.string().trim().min(1, "ຕ້ອງໃສ່ຊື່ຜູ້ສະໜອງ"),
  code: z.string().trim(),
  contactName: z.string().trim(),
  phone: z.string().trim(),
  email: z.string().trim(),
  address: z.string().trim(),
  taxId: z.string().trim(),
  paymentTerms: z.string().trim(),
  notes: z.string().trim(),
  isActive: z.boolean(),
});

function emptyToNull(s: string) {
  return s.trim() ? s.trim() : null;
}

export function SupplierEditPage() {
  const { id } = useParams({ from: "/app/inventory/suppliers/$id/edit" });
  const nav = useNavigate({ from: "/app/inventory/suppliers/$id/edit" });
  const query = useSupplierQuery(id);
  const updateSupplier = useUpdateSupplier(id);

  const form = RHF.useForm<SupplierFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      code: "",
      contactName: "",
      phone: "",
      email: "",
      address: "",
      taxId: "",
      paymentTerms: "",
      notes: "",
      isActive: true,
    },
  });

  useEffect(() => {
    const row = query.data;
    if (!row) return;
    form.reset({
      name: row.name,
      code: row.code ?? "",
      contactName: row.contactName ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      address: row.address ?? "",
      taxId: row.taxId ?? "",
      paymentTerms: row.paymentTerms ?? "",
      notes: row.notes ?? "",
      isActive: row.isActive,
    });
  }, [query.data, form]);

  const onSave = form.handleSubmit(async (values) => {
    try {
      await updateSupplier.mutateAsync({
        name: values.name,
        code: values.code.trim() ? values.code.trim().toUpperCase() : null,
        contactName: emptyToNull(values.contactName),
        phone: emptyToNull(values.phone),
        email: emptyToNull(values.email),
        address: emptyToNull(values.address),
        taxId: emptyToNull(values.taxId),
        paymentTerms: emptyToNull(values.paymentTerms),
        notes: emptyToNull(values.notes),
        isActive: values.isActive,
      });
      toast.success("ບັນທຶກຜູ້ສະໜອງສຳເລັດ");
      nav({ to: "/app/inventory/suppliers" });
    } catch (e: unknown) {
      toast.error(formatSupplierApiError(e));
    }
  });

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: SUPPLIER_MENU_TITLE, to: "/app/inventory/suppliers" },
            { label: query.data?.name ?? "…" },
          ]}
        />
      </Header>
      <Main>
        <QueryState result={query} title="ກຳລັງໂຫຼດ" variant="fullscreen">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂຜູ້ສະໜອງ</h2>
              <p className="text-muted-foreground">{query.data?.name}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/inventory/suppliers" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>

          <FormRoot methods={form} onSubmit={onSave} className="space-y-6">
            <SupplierFormCard form={form} />
          </FormRoot>

          <div className="mt-6 flex flex-wrap justify-between gap-4 border-t pt-6">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/inventory/suppliers" })}
            >
              ຍົກເລີກ
            </Button>
            <Button onClick={onSave} isLoading={updateSupplier.isPending}>
              ບັນທຶກ
            </Button>
          </div>
        </QueryState>
      </Main>
    </>
  );
}
